<template>
  <div
    class="notification"
    :class="notification.type"
    @mouseenter="pauseTimer"
    @mouseleave="resumeTimer"
  >
    <div
      class="icon"
      v-if="icon.show"
      :class="{ 'img-icon': icon.type == 'image' }"
    >
      <img :src="icon.value" v-if="icon.type == 'image'" />
      <i class="fa" :class="icon.value" v-else></i>
    </div>
    <div class="message">
      <div class="title" v-text="notification.title || 'Notification'"></div>
      <div
        class="description"
        v-if="notification.message"
        v-html="notification.message"
      ></div>
      <div class="actions" v-if="actions.length">
        <button
          v-for="action in actions"
          :key="action.id"
          class="btn btn-sm"
          :class="action.btnClass"
          @click.prevent="action.handler"
          v-text="action.title"
        ></button>
      </div>
    </div>
    <button class="close-btn" @click.prevent="dismiss">
      <i class="fa fa-times"></i>
    </button>
  </div>
</template>

<script>
/*
    type Notification = {
        title: string;
        message: undefined|string;
        icon: undefined|string;
        type: undefined|'success'|'error'|'warning'|'info';
        timeout: undefined|number;
        autoDismiss: undefined|boolean;
        actions: undefined|Array<{
            id: null|number|string;
            title: string;
            btnClass: string|null;
            handler: Function|String|Object|null;
        }>;
    };
*/

const defaultTimeout = 8000;

export default {
  props: {
    notification: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      timeout: defaultTimeout,
      timer: null,
    };
  },
  mounted() {
    if (this.notification.autoDismiss == false) {
      return;
    }

    this.timeout = this.notification.timeout || defaultTimeout;

    this.startTimer();

    // check if window is active
    document.addEventListener("visibilitychange", this.visibilityListener);
  },
  methods: {
    dismiss() {
      this.$emit("dismiss", this.notification.id);
      if (this.timer) {
        clearInterval(this.timer);
      }
      document.removeEventListener("visibilitychange", this.visibilityListener);
    },
    startTimer() {
      this.timer = setInterval(() => {
        this.timeout -= 100;
        if (this.timeout <= 0) {
          this.dismiss();
          clearInterval(this.timer);
        }
      }, 100);
    },
    pauseTimer() {
      if (this.timer) {
        clearInterval(this.timer);
      } else {
        this.timeout = this.notification.timeout || defaultTimeout;
      }
    },
    resumeTimer() {
      this.startTimer();
    },
    visibilityListener() {
      setTimeout(() => {
        if (document.visibilityState == "visible") {
          this.resumeTimer();
        } else {
          this.pauseTimer();
        }
      });
    },
  },
  computed: {
    icon() {
      const icon = {
        show: false,
        type: "icon", // icon|image
        value: this.notification.icon || null,
      };

      if (!icon.value || icon.value === "") {
        return icon;
      }

      icon.show = true;

      icon.type = icon.value.indexOf("fa-") === 0 ? "icon" : "image";

      return icon;
    },
    actions() {
      let actions = [];

      if (this.notification.actions) {
        actions = this.notification.actions.map((action) => {
          let handler = () => {
            setTimeout(() => {
              this.dismiss();
            }, 100);
          };

          if (action.handler) {
            if (typeof action.handler === "function") {
              handler = () => {
                action.handler();
                setTimeout(() => {
                  this.dismiss();
                });
              };
            } else {
              handler = () => {
                this.$router.push(action.handler);
                setTimeout(() => {
                  this.dismiss();
                });
              };
            }
          }

          return {
            id: this.uuid(),
            title: action.title || "Action",
            btnClass: action.btnClass || "btn-light",
            handler: handler,
          };
        });
      }

      return actions;
    },
  },
};
</script>
<style lang="scss" scoped>
.notification {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: var(--border-radius);
  background-color: var(--bg-translucent);
  box-shadow: var(--box-shadow-light);
  transition: all 0.3s ease-in-out;
  backdrop-filter: blur(20px) saturate(180%);
  border: 1.2px solid var(--lighter);
  gap: 0.8rem;

  .icon {
    --icon-size: 2.5rem;
    --icon-bg: var(--primary);
    --icon-color: var(--primary-contrast);

    flex: 0 0 var(--icon-size);
    height: var(--icon-size);
    width: var(--icon-size);
    border-radius: 25%;
    background-color: var(--icon-bg);
    color: var(--icon-color);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.2rem;
    overflow: hidden;
    margin-bottom: auto;

    i {
      font-size: calc(var(--icon-size) * 0.6);
      color: var(--icon-color);
    }

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }

    @media (max-width: 575px) {
      --icon-size: 2.1rem;
    }

    &.img-icon {
      border-radius: 20%;
      --icon-bg: var(--lighter) !important;
    }
  }

  .message {
    flex: 1 1 auto;

    .title {
      font-size: 1.1rem;
      font-weight: bold;
      color: var(--dark);
    }

    .description {
      font-size: 0.8rem;
      font-weight: 400;
      color: var(--dark);
      opacity: 0.8;
    }
  }

  .close-btn {
    flex: 0 0 1.8rem;
    height: 1.8rem;
    padding: 0;
    border: none;
    cursor: pointer;
    transition: var(--transition);
    font-size: 0.7rem;
    border-radius: 50%;
    background: var(--lighter);
    color: var(--font-color);
    margin-bottom: auto;

    &:hover {
      color: var(--primary);
    }
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.6rem;
    .btn {
      padding: 0.3rem 0.6rem;
      line-height: 1.1;
      font-weight: 600;
    }
  }

  &.success .icon {
    --icon-bg: var(--success);
    --icon-color: var(--success-contrast);
  }

  &.error .icon,
  &.danger .icon {
    --icon-bg: var(--danger);
    --icon-color: var(--danger-contrast);
  }

  &.warning .icon {
    --icon-bg: var(--warning);
    --icon-color: var(--warning-contrast);
  }

  &.info .icon {
    --icon-bg: var(--info);
    --icon-color: var(--info-contrast);
  }
}
</style>